import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock, BookOpen } from "lucide-react";
import { PUBLISHED_POSTS, formatPostDate, getPostImage } from "@/lib/blog-posts";

export default function BlogPreview() {
  const posts = PUBLISHED_POSTS.slice(0, 3);
  if (posts.length === 0) return null;

  return (
    <section
      id="blog"
      data-testid="blog-preview-section"
      className="py-20 lg:py-28 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-10">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-blue inline-flex items-center gap-1.5">
              <BookOpen className="h-3.5 w-3.5" /> Oxford Journal
            </p>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-brand-navy leading-tight">
              Care tips from your
              <span className="block text-brand-blue">Chandler experts.</span>
            </h2>
          </div>
          <Link
            to="/blog"
            data-testid="blog-preview-see-all"
            className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-navy hover:text-brand-blue"
          >
            See all posts <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => {
            const img = getPostImage(p);
            return (
              <article
                key={p.slug}
                data-testid={`blog-preview-card-${p.slug}`}
                className="group rounded-3xl overflow-hidden border border-slate-200 bg-white hover:shadow-xl transition-all duration-300"
              >
                <Link to={`/blog/${p.slug}`} className="block">
                  <div className="aspect-[4/3] bg-slate-100 overflow-hidden">
                    {img ? (
                      <img
                        src={img}
                        alt={p.title}
                        className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
                    ) : (
                      <div className="h-full w-full bg-gradient-to-br from-brand-navy to-brand-blue" />
                    )}
                  </div>
                </Link>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar className="h-3 w-3" /> {formatPostDate(p.date)}
                    </span>
                    {p.readingMinutes && (
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="h-3 w-3" /> {p.readingMinutes} min
                      </span>
                    )}
                  </div>
                  <h3 className="mt-2.5 font-display text-lg font-bold text-brand-navy leading-tight">
                    <Link
                      to={`/blog/${p.slug}`}
                      className="hover:text-brand-blue transition-colors"
                    >
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <Link
                    to={`/blog/${p.slug}`}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-brand-blue hover:text-brand-navy"
                  >
                    Read more <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
